import { Check, ChevronsUpDown, Building2, Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export const WorkspaceSwitcher = () => {
  const { currentWorkspace, workspaces, setCurrentWorkspace, isAgency, isAdmin, user } = useAuth();
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  // Don't show workspace switcher for admin-only users or non-agency users
  if (!isAgency || (isAdmin && !isAgency)) {
    return null;
  }

  const handleWorkspaceChange = async (workspaceId: string) => {
    const selectedWorkspace = workspaces.find(w => w.id === workspaceId);
    if (!selectedWorkspace) return;

    setSwitching(true);
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({ current_workspace_id: workspaceId })
        .eq('id', user?.id);
      
      if (error) throw error;

      setCurrentWorkspace(selectedWorkspace);
      setOpen(false);

      // CRITICAL FIX #40: No navigation - let React Query refetch data
      toast({
        title: "Workspace switched",
        description: `Now viewing ${selectedWorkspace.client_name}'s workspace`,
      });
      
      // Context updated - React Query will refetch all workspace-dependent queries
    } catch (error) {
      console.error('Error switching workspace:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to switch workspace. Please try again.",
      });
    } finally {
      setSwitching(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-[200px] justify-between"
          disabled={switching}
        >
          <Building2 className="mr-2 h-4 w-4" />
          <span className="truncate">
            {switching ? "Switching..." : (currentWorkspace?.client_name || "Select workspace")}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[240px] p-0">
        <Command>
          <CommandInput placeholder="Search workspace..." />
          <CommandList>
            <CommandEmpty>No workspace found.</CommandEmpty>
            <CommandGroup heading="Your Workspaces">
              {workspaces.map((workspace) => (
                <CommandItem
                  key={workspace.id}
                  value={workspace.id}
                  onSelect={() => handleWorkspaceChange(workspace.id)}
                  disabled={switching}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      currentWorkspace?.id === workspace.id
                        ? "opacity-100"
                        : "opacity-0"
                    )}
                  />
                  <div className="flex flex-col">
                    <span className="font-medium">{workspace.client_name}</span>
                    {workspace.company_name && (
                      <span className="text-xs text-muted-foreground">
                        {workspace.company_name}
                      </span>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandGroup>
              <CommandItem
                onSelect={() => {
                  setOpen(false);
                  navigate('/workspaces');
                }}
                className="text-primary"
              >
                <Plus className="mr-2 h-4 w-4" />
                <span className="font-medium">Create New Workspace</span>
              </CommandItem>
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
